import { http } from "~/utils";

/**
 * Run module service — HTTP calls for Run reports and the SSOT Web Form flow.
 * All methods use the authenticated `http` client.
 */
export default {
    /**
     * Fetches the Site vs Run report (aggregated counts per site and run status).
     *
     * @param {Object} filters - Filter parameters (dates, OET, etc.).
     * @returns {Promise<Object>} Axios response with report data.
     */
    getSiteVsRunReport: async filters => {
        return http.post(`/india/run/site-vs-run-report/`, filters).then(response => {
            return response;
        });
    },
    /**
     * Downloads the Open Run vs Pendency report as an Excel (.xlsx) file.
     * Triggers a browser download automatically via a temporary anchor element.
     *
     * @param {Object} filters - Filter parameters.
     * @returns {Promise<void>}
     */
    getOpenRunVsPendencyReport: async filters => {
        return http
            .post(`/india/run/open-run-vs-pendency-report/`, filters, { responseType: "blob" })
            .then(response => {
                const filename = "Pendency report.xlsx";
                if (response) {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", filename);
                    document.body.appendChild(link);
                    link.click();
                }
            });
    },
    /**
     * Retrieves the bypass history for OET items.
     *
     * @param {Object} filters - Query parameters (e.g. oet, run, page).
     * @returns {Promise<Object>} Paginated response data.
     */
    getBypassHistory: async filters => {
        return http.get(`/india/oet-items-bypass/`, { params: filters }).then(response => {
            return response.data;
        });
    },
    /**
     * Searches NDP-d for items matching the SSOT Web Form filters.
     * Used to populate the item table before final submission.
     *
     * @param {Object} filters - Payload built by `formatSubmitPayload`.
     * @param {number}   filters.activity - Activity ID.
     * @param {string[]} filters.customer_site_codes - List of site codes to search.
     * @param {string}   filters.customer_request_delivery_date - ISO date string.
     * @param {string}   filters.project_request_delivery_date - ISO date string.
     * @returns {Promise<{data: Array<Object>, errors: string[]}>}
     */
    ndpdSearch: async filters => {
        return http.post(`/india/ndpd-search/`, filters).then(response => {
            return response.data;
        });
    },
    /**
     * Submits the final SSOT items to create / update a Service Order Template in the backend.
     * Returns the importer record, whose `id` is then used to trigger the validation step.
     *
     * @param {number}        oet_id    - The OET ID.
     * @param {number}        run_id    - The Run ID.
     * @param {Array<Object>} data      - Normalised table rows from `SSOTItemTable`.
     * @param {Object}        form_data - Header form payload (activity, dates, site codes).
     * @returns {Promise<{id: number}>} The created importer record.
     */
    sendSSOTItems: async (oet_id, run_id, data, form_data) => {
        const payload = { oet_id, run_id, data, form_data, source: "web_form" };
        return http.post("/india/ssot-create/", payload).then(response => {
            return response.data;
        });
    },
    /**
     * Fetches vendor (subcontractor / supplier) autocomplete options.
     * Searches by vendor number when the query is numeric, otherwise by legal name.
     *
     * @param {string} [search=""] - The search string.
     * @returns {Promise<string[]>} Strings in format `"VENDOR_NUMBER - LEGAL_NAME"`.
     */
    vendorAutocomplete: async (search = "") => {
        const request_filters = search.length > 0
            ? (/^\d+$/.test(search) ? { vendor_number: search } : { legal_name: search })
            : {};
        return http
            .get("/suppliers/company/vendor-numbers/", {
                params: request_filters
            })
            .then(response =>
                response.data.results.map(item => `${item.vendor_number} - ${item.legal_name}`)
            );
    },
    /**
     * Fetches SOG (Service Order Group) autocomplete options.
     *
     * @param {string} [search=""] - The search string.
     * @returns {Promise<string[]>} List of SOG value strings.
     */
    sogAutocomplete: async (search = "") => {
        const request_filters = search.length > 0
            ? { search }
            : {};
        return http
            .get("/activity/sog-form/", {
                params: request_filters
            }).then(response => response.data.results.map(item => item.value));
    }
};
